/**
 * Container Highlight Utilities for UI-Model-1
 *
 * Provides hierarchical highlighting for containers based on selection state.
 * When a component or nested container is selected, the entire container
 * ancestry is highlighted with different colors:
 * - Primary (blue): Selected item/innermost container
 * - Secondary (purple): Immediate parent container
 * - Tertiary (grey): Grandparent and higher ancestors
 */

import { UIModelState } from '../types/state.types';
import { selectContainerPath } from '../selectors';

/**
 * Highlight level for containers in the selection hierarchy.
 * - 'primary': The selected container itself (blue)
 * - 'secondary': Immediate parent of selected container (purple)
 * - 'tertiary': Grandparent and higher ancestors (grey)
 * - null: Not in the selection hierarchy
 */
export type ContainerHighlightLevel = 'primary' | 'secondary' | 'tertiary' | null;

/**
 * Build a map of container IDs to their highlight levels based on current selection.
 *
 * @param state - The current UI model state
 * @param selectedContainerId - The ID of the currently selected container
 * @returns Map of container IDs to their highlight levels
 *
 * @example
 * // If Container 1.1.1 is selected:
 * // - Container 1.1.1 -> 'primary' (blue)
 * // - Container 1.1 -> 'secondary' (purple)
 * // - Container 1 -> 'tertiary' (grey)
 */
export function buildHighlightMap(
  state: UIModelState,
  selectedContainerId: string | null
): Map<string, ContainerHighlightLevel> {
  const highlightMap = new Map<string, ContainerHighlightLevel>();

  if (!selectedContainerId) return highlightMap;

  // Mark the selected container as primary
  highlightMap.set(selectedContainerId, 'primary');

  // Get the ancestor path from root to selected container
  const selectedPath = selectContainerPath(state, selectedContainerId);

  // selectedPath includes the selected container as the last element
  // We need to mark parents based on their distance from the selected container
  for (let i = 0; i < selectedPath.length - 1; i++) {
    const container = selectedPath[i];
    // Distance from selected: selectedPath.length - 1 - i
    // i=0 is root, i=length-1 is selected
    const distanceFromSelected = selectedPath.length - 1 - i;

    if (distanceFromSelected === 1) {
      // Immediate parent -> secondary (purple)
      highlightMap.set(container.id, 'secondary');
    } else {
      // Grandparent or higher -> tertiary (grey)
      highlightMap.set(container.id, 'tertiary');
    }
  }

  return highlightMap;
}

/**
 * Get CSS classes for a top-level container based on its highlight level.
 * Includes support for drag-over state which takes precedence.
 *
 * @param level - The highlight level of the container
 * @param isDragOver - Whether an item is being dragged over this container
 * @returns Tailwind CSS classes for the container styling
 */
export function getHighlightClasses(
  level: ContainerHighlightLevel,
  isDragOver: boolean = false
): string {
  // Drag-over state takes precedence
  if (isDragOver) {
    return 'tw-border-accent tw-bg-accent-tint tw-ring-1 tw-ring-accent';
  }

  switch (level) {
    case 'primary':
      // Selected container - accent highlight
      return 'tw-border-accent tw-ring-1 tw-ring-accent tw-bg-surface';
    case 'secondary':
      // Immediate parent - the purple tone-accent pair (themes with data-theme)
      return 'tw-border-tone-accent-ink tw-ring-1 tw-ring-tone-accent-ink tw-bg-surface';
    case 'tertiary':
      // Grandparent+ - grey highlight
      return 'tw-border-edge-strong tw-bg-surface-sunken';
    default:
      // Not in selection hierarchy - default styling
      return 'tw-border-edge tw-bg-surface hover:tw-border-edge-strong';
  }
}

/**
 * Get CSS classes for nested containers based on their highlight level.
 * Slightly different styling to distinguish from top-level containers.
 *
 * @param level - The highlight level of the container
 * @param isDragOver - Whether an item is being dragged over this container
 * @returns Tailwind CSS classes for the nested container styling
 */
export function getNestedHighlightClasses(
  level: ContainerHighlightLevel,
  isDragOver: boolean = false
): string {
  // Drag-over state takes precedence
  if (isDragOver) {
    return 'tw-border-accent tw-bg-accent-tint tw-ring-1 tw-ring-accent';
  }

  switch (level) {
    case 'primary':
      // Selected nested container - accent highlight on the sunken ground
      return 'tw-border-accent tw-ring-1 tw-ring-accent tw-bg-surface-sunken';
    case 'secondary':
      // Immediate parent - the purple tone-accent pair (themes with data-theme)
      return 'tw-border-tone-accent-ink tw-ring-1 tw-ring-tone-accent-ink tw-bg-surface-sunken';
    case 'tertiary':
      // Grandparent+ - grey highlight
      return 'tw-border-edge-strong tw-bg-surface-sunken';
    default:
      // Not in selection hierarchy - default nested styling
      return 'tw-border-edge tw-bg-surface-sunken hover:tw-border-edge-strong';
  }
}
